import React, { useContext, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import html2pdf from "html2pdf.js";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { pdfjs } from "react-pdf";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Switch
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import ReplayIcon from "@material-ui/icons/Replay";
import SearchIcon from "@material-ui/icons/Search";
import VisibilityIcon from "@material-ui/icons/Visibility";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import DescriptionIcon from "@material-ui/icons/Description";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles(theme => ({
  hero: {
    padding: theme.spacing(3),
    borderRadius: 24,
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, rgba(14,27,40,0.98) 0%, rgba(21,75,117,0.96) 100%)"
        : "linear-gradient(135deg, rgba(239,246,255,1) 0%, rgba(215,234,250,1) 100%)",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.08)"
      : "1px solid rgba(28,78,125,0.08)",
    boxShadow: "0 24px 60px rgba(16, 63, 104, 0.14)",
    marginBottom: theme.spacing(3)
  },
  metricCard: {
    borderRadius: 22,
    padding: theme.spacing(2.3),
    minHeight: 120,
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(160deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)"
        : "linear-gradient(160deg, rgba(255,255,255,0.96) 0%, rgba(246,250,255,0.92) 100%)",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid rgba(28,78,125,0.08)",
    boxShadow: "0 18px 40px rgba(16, 63, 104, 0.08)"
  },
  filterCard: {
    borderRadius: 24,
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2.5),
    boxShadow: "0 18px 40px rgba(16, 63, 104, 0.08)"
  },
  tableCard: {
    borderRadius: 24,
    overflow: "hidden",
    boxShadow: "0 18px 40px rgba(16, 63, 104, 0.08)"
  },
  tableWrap: {
    overflowX: "auto"
  },
  tableHead: {
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.03)"
        : "linear-gradient(180deg, rgba(245,249,253,1) 0%, rgba(235,243,250,1) 100%)"
  },
  th: {
    fontSize: "0.78rem",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    fontWeight: 700,
    whiteSpace: "nowrap"
  },
  row: {
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.025)" : "rgba(28,78,125,0.03)"
    }
  },
  idCell: {
    fontWeight: 700,
    color: theme.palette.primary.main
  },
  nameBlock: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    minWidth: 220
  },
  actionButton: {
    minWidth: 36
  },
  detailSection: {
    borderRadius: 18,
    padding: theme.spacing(2),
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.08)"
      : "1px solid rgba(28,78,125,0.08)",
    background: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.02)"
      : "rgba(247,250,253,0.9)"
  },
  detailMetric: {
    borderRadius: 16,
    padding: theme.spacing(1.75),
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid rgba(28,78,125,0.08)",
    background: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.03)"
      : "#fff"
  },
  compactList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.2)
  },
  compactRow: {
    padding: theme.spacing(1.5),
    borderRadius: 14,
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid rgba(28,78,125,0.08)",
    background: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.025)"
      : "#fff"
  }
}));

const emptyClientForm = {
  id: null,
  name: "",
  identificationNumber: "",
  installationCode: "",
  dv: "",
  email: "",
  phone: "",
  address: "",
  stateName: "",
  merchantRegistration: "",
  softwareClassificationCode: "",
  commercialClassificationCode: "",
  catalogSelections: {
    tax: null,
    environment: null,
    payroll_environment: null,
    eqdocs_environment: null,
    operation: null,
    liability: null
  },
  primaryNotificationPhone: "",
  primaryNotificationEmail: "",
  notes: "",
  isActive: true
};

const emptyEstablishmentForm = {
  id: null,
  installationCode: "",
  name: "",
  address: "",
  departmentCode: "",
  departmentName: "",
  municipalityCode: "",
  municipalityName: "",
  primaryPhone: "",
  primaryEmail: "",
  notes: "",
  isActive: true
};

const emptyRutOnboardingForm = {
  sourceId: "",
  rutFile: null,
  resolutionFiles: [],
  certificateFile: null,
  certificatePassword: "",
  logoFile: null,
  invoice: true,
  payroll: false,
  eqdocs: false,
  softwareClassificationCode: "",
  commercialClassificationCode: "",
  setupEnvironment: true,
  setupTestResolutions: true,
  softwareName: "",
  softwareId: "",
  softwarePin: "12345",
  runInvoiceTests: false,
  runPayrollTests: false,
  invoiceTestSetId: "",
  payrollTestSetId: "",
  invoiceTestStartNumber: "",
  creditNoteTestNumber: "",
  debitNoteTestNumber: "",
  completeExisting: false,
  updateExisting: false,
  overridesJson: "",
  resolutionsJson: "",
  showAdvanced: false,
  confirmCreate: false
};

const emptyApiEnvironmentForm = {
  invoice_environment: "",
  payroll_environment: "",
  eqdocs_environment: ""
};

const emptyApiPlanForm = {
  service: "invoice",
  plan_id: "",
  absolut_plan_documents: "",
  renew: true,
  notes: ""
};

const RUT_ONBOARDING_DRAFT_KEY = "soluwhats.rut-onboarding-draft.v1";

const readRutOnboardingDraft = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(RUT_ONBOARDING_DRAFT_KEY) || "null");
    return stored && typeof stored === "object" ? stored : null;
  } catch (_error) {
    return null;
  }
};

const persistRutOnboardingDraft = form => {
  const { rutFile, certificateFile, logoFile, resolutionFiles, ...draft } = form;
  window.localStorage.setItem(RUT_ONBOARDING_DRAFT_KEY, JSON.stringify({ ...draft, savedAt: new Date().toISOString() }));
};

const clearRutOnboardingDraft = () => window.localStorage.removeItem(RUT_ONBOARDING_DRAFT_KEY);

const RutHelpLabel = ({ label, help }) => (
  <Box component="span" display="inline-flex" alignItems="center" gridGap={3}>
    {label}
    <Tooltip title={help} arrow placement="top">
      <HelpOutlineIcon style={{ fontSize: 16, cursor: "help", opacity: 0.75 }} />
    </Tooltip>
  </Box>
);

// Uses the same worker source already used for message PDF previews.
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const fileToBase64 = file => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || "").split(",").pop() || "");
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const RUT_MISSING_FIELD_LABELS = {
  identification_number: "NIT o cedula",
  dv: "digito de verificacion",
  business_name: "nombre o razon social",
  address: "direccion",
  email: "correo",
  phone: "telefono",
  municipality_id: "municipio"
};

const getRutResultPayload = result => result?.result || result?.data || result || {};

const getRutMissingFields = result => {
  const payload = getRutResultPayload(result);
  const fields = payload?.missing || result?.missing || result?.errors;
  if (Array.isArray(fields)) return fields;
  if (fields && typeof fields === "object") return Object.keys(fields);
  return [];
};

const formatRutMissingFields = result => getRutMissingFields(result)
  .map(field => RUT_MISSING_FIELD_LABELS[field] || field)
  .join(", ");

const inspectResolutionPdf = async file => {
  if (!/\.pdf$/i.test(file.name) || file.size > 8 * 1024 * 1024) {
    throw new Error("Cada resolucion debe ser un PDF de maximo 8 MB.");
  }

  const binary = await file.arrayBuffer();
  const signature = new TextDecoder("latin1").decode(binary.slice(0, 5));
  if (signature !== "%PDF-") {
    throw new Error(`${file.name} no contiene un PDF valido.`);
  }

  let documentProxy;
  try {
    documentProxy = await pdfjs.getDocument({ data: binary.slice(0) }).promise;
    const maxPages = Math.min(documentProxy.numPages, 3);
    let text = "";
    for (let pageNumber = 1; pageNumber <= maxPages; pageNumber += 1) {
      const page = await documentProxy.getPage(pageNumber);
      const content = await page.getTextContent();
      text += content.items.map(item => item.str || "").join(" ");
    }
    const normalizedText = text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
    if (normalizedText.trim().length < 30) {
      throw new Error(`${file.name} parece una imagen o escaneo sin texto legible.`);
    }
    const hasDianIssuer = /\bDIAN\b|DIRECCION\s+DE\s+IMPUESTOS|U\.?A\.?(E)?\.?/.test(normalizedText);
    const resolutionMarkers = [
      "RESOLUCION",
      "RANGOS DE NUMERACION",
      "FACTURA ELECTRONICA",
      "HABILITACION",
      "PREFIJO",
      "DESDE EL NUMERO",
      "HASTA EL NUMERO"
    ].filter(marker => normalizedText.includes(marker)).length;

    // DIAN has more than one official PDF layout. Some resolutions are headed
    // by "Rangos de numeracion" and "Habilitacion" rather than "Resolucion".
    if (!hasDianIssuer || resolutionMarkers < 2) {
      throw new Error(`${file.name} no parece una resolucion DIAN legible.`);
    }
    return { file, pages: documentProxy.numPages };
  } catch (error) {
    if (error?.name === "PasswordException") {
      throw new Error(`${file.name} esta protegido con clave y no puede procesarse.`);
    }
    throw error;
  } finally {
    documentProxy?.destroy?.();
  }
};

const metricStyle = active => ({
  backgroundColor: active ? "rgba(29,154,108,0.14)" : "rgba(214,69,69,0.14)",
  color: active ? "#1D9A6C" : "#D64545",
  fontWeight: 700
});

const formatDisplayDate = value => {
  if (!value) {
    return "sin fecha";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleDateString("es-CO");
};

const normalizeCatalogLabel = value => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .trim()
  .toUpperCase();

const canSyncResolutionToNube = customer => {
  const classification = normalizeCatalogLabel(customer?.softwareClassificationCode);
  return classification === "FACILWEB NUBE" || classification === "FACILWEB NUBE + NOMINA";
};

const catalogValue = (customer, catalogType) =>
  (customer?.catalogValues || []).find(item => String(item.catalogType || "") === catalogType) || null;

const catalogEnvironmentValue = (customer, catalogType) => {
  const value = catalogValue(customer, catalogType);
  const code = String(value?.catalogCode || "").trim();
  const name = normalizeCatalogLabel(value?.catalogName);
  if (code === "1" || name.includes("PRODUCCION")) return "production";
  if (code === "2" || name.includes("HABILITACION")) return "habilitation";
  return "";
};

const environmentLabel = value => {
  if (value === "production") return "Produccion";
  if (value === "habilitation") return "Habilitacion";
  return "Sin dato";
};

const CLIENT_EDITABLE_CATALOG_LABELS = {
  tax: "Impuestos",
  operation: "Operacion",
  liability: "Responsable de IVA"
};

const CLIENT_CATALOG_LABELS = {
  ...CLIENT_EDITABLE_CATALOG_LABELS,
  environment: "Ambiente FE",
  payroll_environment: "Ambiente nomina",
  eqdocs_environment: "Ambiente documento soporte",
  language: "Idioma",
  document_identification: "Tipo documento",
  country: "Pais",
  currency: "Moneda",
  organization: "Organizacion",
  regime: "Regimen",
  department: "Departamento",
  municipality: "Municipio"
};

const hasUsableClientContactName = contact => {
  const name = String(contact?.name || "").trim();
  const number = String(contact?.number || "").trim();

  if (!name) return false;
  if (!/^\d+$/.test(name)) return true;
  if (!number) return false;

  return name !== number && name.length < 12;
};

const buildClientContactLabel = contact => {
  if (!contact) return "";

  const rawName = String(contact.name || "").trim();
  const rawNumber = String(contact.number || "").trim();
  const name = hasUsableClientContactName(contact) ? rawName : "";

  if (name && rawNumber) {
    return `${name} - ${rawNumber}`;
  }

  return name || rawNumber || `Contacto #${contact.id}`;
};

const Clients = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.super || user?.profile === "admin";

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [debouncedSearchParam, setDebouncedSearchParam] = useState("");
  const [softwareClassification, setSoftwareClassification] = useState("");
  const [commercialClassification, setCommercialClassification] = useState("");
  const [activeFilter, setActiveFilter] = useState("active");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("DESC");
  const [catalogs, setCatalogs] = useState({
    software: [],
    commercial: [],
    attributes: {
      tax: [],
      environment: [],
      payroll_environment: [],
      eqdocs_environment: [],
      operation: [],
      liability: []
    }
  });
  const [geoCatalogs, setGeoCatalogs] = useState({ departments: [], municipalities: [] });
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [count, setCount] = useState(0);
  const [exporting, setExporting] = useState(false);

  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [clientSaving, setClientSaving] = useState(false);
  const [clientForm, setClientForm] = useState(emptyClientForm);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);

  const [establishmentModalOpen, setEstablishmentModalOpen] = useState(false);
  const [establishmentSaving, setEstablishmentSaving] = useState(false);
  const [establishmentForm, setEstablishmentForm] = useState(emptyEstablishmentForm);
  const [contactLinkDialog, setContactLinkDialog] = useState(false);
  const [contactLinkSelection, setContactLinkSelection] = useState([]);
  const [contactLinkOptions, setContactLinkOptions] = useState([]);
  const [contactLinkLoading, setContactLinkLoading] = useState(false);
  const [rutOnboardingOpen, setRutOnboardingOpen] = useState(false);
  const [rutSources, setRutSources] = useState([]);
  const [rutOnboardingForm, setRutOnboardingForm] = useState(emptyRutOnboardingForm);
  const [rutOnboardingSaving, setRutOnboardingSaving] = useState(false);
  const [rutOnboardingResult, setRutOnboardingResult] = useState(null);
  const [rutOnboardingHelpOpen, setRutOnboardingHelpOpen] = useState(false);
  const [rutOnboardingDraftRestored, setRutOnboardingDraftRestored] = useState(false);
  const [clientActionAnchor, setClientActionAnchor] = useState(null);
  const [clientActionRecord, setClientActionRecord] = useState(null);
  const [resolutionDialogOpen, setResolutionDialogOpen] = useState(false);
  const [resolutionCustomer, setResolutionCustomer] = useState(null);
  const [resolutionHistory, setResolutionHistory] = useState([]);
  const [resolutionLoading, setResolutionLoading] = useState(false);
  const [resolutionProcessing, setResolutionProcessing] = useState(false);
  const [resolutionFile, setResolutionFile] = useState(null);
  const [syncResolutionToNube, setSyncResolutionToNube] = useState(false);
  const [nubeSyncConfirmation, setNubeSyncConfirmation] = useState(null);
  const [nubeSyncApplying, setNubeSyncApplying] = useState(false);
  const [resolutionHelpOpen, setResolutionHelpOpen] = useState(false);
  const [resolutionDetailOpen, setResolutionDetailOpen] = useState(false);
  const [resolutionDetail, setResolutionDetail] = useState(null);
  const [apiOperationsOpen, setApiOperationsOpen] = useState(false);
  const [apiOperationsCustomer, setApiOperationsCustomer] = useState(null);
  const [apiOperationsStatus, setApiOperationsStatus] = useState(null);
  const [apiOperationsPlans, setApiOperationsPlans] = useState(null);
  const [apiOperationsLoading, setApiOperationsLoading] = useState(false);
  const [apiOperationsSaving, setApiOperationsSaving] = useState(false);
  const [apiOperationsTab, setApiOperationsTab] = useState(0);
  const [apiEnvironmentForm, setApiEnvironmentForm] = useState(emptyApiEnvironmentForm);
  const [apiPlanForm, setApiPlanForm] = useState(emptyApiPlanForm);
  const [syncAllApiLoading, setSyncAllApiLoading] = useState(false);

  const activeCount = useMemo(() => records.filter(item => item.isActive).length, [records]);
  const manualCount = useMemo(
    () => records.filter(item => String(item.originCode || "").toLowerCase() === "manual").length,
    [records]
  );

  const totalPages = useMemo(() => {
    if (pageSize === -1) {
      return 1;
    }
    return Math.max(Math.ceil(count / pageSize), 1);
  }, [count, pageSize]);

  const loadCatalogs = async () => {
    try {
      const [{ data }, { data: geoData }] = await Promise.all([
        api.get("/clients/catalogs"),
        api.get("/clients/geo/catalogs")
      ]);
      setCatalogs({
        software: Array.isArray(data?.software) ? data.software : [],
        commercial: Array.isArray(data?.commercial) ? data.commercial : [],
        attributes: data?.attributes || {
          tax: [],
          environment: [],
          payroll_environment: [],
          eqdocs_environment: [],
          operation: [],
          liability: []
        }
      });
      setGeoCatalogs({
        departments: Array.isArray(geoData?.departments) ? geoData.departments : [],
        municipalities: Array.isArray(geoData?.municipalities) ? geoData.municipalities : []
      });
    } catch (error) {
      if (error?.response?.status !== 403) {
        toastError(error);
      }
    }
  };

  const loadRecords = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/clients", {
        params: {
          searchParam: debouncedSearchParam,
          softwareClassification,
          commercialClassification,
          activeFilter,
          sortBy,
          sortDirection,
          pageNumber,
          pageSize: pageSize === -1 ? "all" : pageSize
        }
      });

      setRecords(Array.isArray(data?.records) ? data.records : []);
      setCount(Number(data?.count || 0));
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = rows => {
    const exportRows = rows.map(item => ({
      ID: item.id,
      Cliente: item.name || "",
      NIT_Cedula: `${item.identificationNumber || ""}${item.dv ? `-${item.dv}` : ""}`,
      Correo: item.email || "",
      Telefono: item.phone || item.primaryNotificationPhone || "",
      Direccion: item.address || "",
      Estado: item.stateName || "",
      Software: item.softwareClassificationCode || "",
      ClasificacionComercial: item.commercialClassificationCode || "",
      Activo: item.isActive ? "Si" : "No",
      Origen: item.originCode || "",
      Fuente: item.integrationSource?.name || "",
      Actualizado: item.updatedAt ? new Date(item.updatedAt).toLocaleString("es-CO") : ""
    }));

    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Clientes");
    XLSX.writeFile(wb, `clientes-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportPdf = async rows => {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = `
      <div style="padding:24px;font-family:Arial,sans-serif;color:#1f2937;">
        <h1 style="margin:0 0 8px;">Listado de clientes</h1>
        <p style="margin:0 0 18px;">Generado: ${new Date().toLocaleString("es-CO")}</p>
        <table style="width:100%;border-collapse:collapse;font-size:11px;">
          <thead>
            <tr>
              <th style="border:1px solid #cbd5e1;padding:8px;text-align:left;">Cliente</th>
              <th style="border:1px solid #cbd5e1;padding:8px;text-align:left;">Documento</th>
              <th style="border:1px solid #cbd5e1;padding:8px;text-align:left;">Software</th>
              <th style="border:1px solid #cbd5e1;padding:8px;text-align:left;">Clasificacion</th>
              <th style="border:1px solid #cbd5e1;padding:8px;text-align:left;">Correo</th>
              <th style="border:1px solid #cbd5e1;padding:8px;text-align:left;">Telefono</th>
            </tr>
          </thead>
          <tbody>
            ${rows.map(item => `
              <tr>
                <td style="border:1px solid #cbd5e1;padding:8px;">${item.name || ""}</td>
                <td style="border:1px solid #cbd5e1;padding:8px;">${item.identificationNumber || ""}${item.dv ? `-${item.dv}` : ""}</td>
                <td style="border:1px solid #cbd5e1;padding:8px;">${item.softwareClassificationCode || ""}</td>
                <td style="border:1px solid #cbd5e1;padding:8px;">${item.commercialClassificationCode || ""}</td>
                <td style="border:1px solid #cbd5e1;padding:8px;">${item.email || ""}</td>
                <td style="border:1px solid #cbd5e1;padding:8px;">${item.phone || item.primaryNotificationPhone || ""}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      </div>
    `;

    await html2pdf()
      .set({
        margin: 8,
        filename: `clientes-${new Date().toISOString().slice(0, 10)}.pdf`,
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" }
      })
      .from(wrapper)
      .save();
  };

  const handleExport = async format => {
    try {
      setExporting(true);
      const { data } = await api.get("/clients/export", {
        params: {
          searchParam: debouncedSearchParam,
          softwareClassification,
          commercialClassification
          ,
          activeFilter,
          sortBy,
          sortDirection
        }
      });

      const rows = Array.isArray(data) ? data : [];
      if (!rows.length) {
        toast.warning("No hay clientes para exportar.");
        return;
      }

      if (format === "xlsx") {
        exportExcel(rows);
      } else {
        await exportPdf(rows);
      }
    } catch (error) {
      toastError(error);
    } finally {
      setExporting(false);
    }
  };

  const reloadDetailRecord = async clientId => {
    const { data } = await api.get(`/clients/${clientId}`);
    setDetailRecord(data);
  };

  useEffect(() => {
    loadCatalogs();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchParam(searchParam);
      setPageNumber(1);
    }, 350);

    return () => clearTimeout(timeout);
  }, [searchParam]);

  useEffect(() => {
    setPageNumber(1);
  }, [softwareClassification, commercialClassification, activeFilter, sortBy, sortDirection, pageSize]);

  useEffect(() => {
    loadRecords();
  }, [debouncedSearchParam, softwareClassification, commercialClassification, activeFilter, sortBy, sortDirection, pageNumber, pageSize]);

  const openCreateClientModal = () => {
    setClientForm(emptyClientForm);
    setClientModalOpen(true);
  };

  const openEditClientModal = async record => {
    let detailData = null;

    try {
      const { data } = await api.get(`/clients/${record.id}`);
      detailData = data;
    } catch (_error) {
      detailData = null;
    }

    setClientForm({
      id: record.id,
      name: record.name || "",
      identificationNumber: record.identificationNumber || "",
      installationCode: record.installationCode || detailData?.installationCode || "",
      dv: record.dv || "",
      email: record.email || "",
      phone: record.phone || "",
      address: record.address || "",
      stateName: record.stateName || "",
      merchantRegistration: record.merchantRegistration || "",
      softwareClassificationCode: record.softwareClassificationCode || "",
      commercialClassificationCode: record.commercialClassificationCode || "",
      catalogSelections: detailData?.editableCatalogSelections || emptyClientForm.catalogSelections,
      primaryNotificationPhone: record.primaryNotificationPhone || "",
      primaryNotificationEmail: record.primaryNotificationEmail || "",
      notes: record.notes || "",
      isActive: record.isActive !== false
    });
    setClientModalOpen(true);
  };

  const handleSaveClient = async () => {
    try {
      const payload = {
        ...clientForm,
        name: String(clientForm.name || "").trim(),
        identificationNumber: String(clientForm.identificationNumber || "").trim()
      };

      if (!payload.name || !payload.identificationNumber) {
        toast.error("Nombre y NIT o cedula son obligatorios.");
        return;
      }

      setClientSaving(true);

      if (clientForm.id) {
        await api.put(`/clients/${clientForm.id}`, payload);
        toast.success("Cliente actualizado.");
      } else {
        await api.post("/clients", payload);
        toast.success("Cliente creado.");
      }

      setClientModalOpen(false);
      await loadRecords(true);
    } catch (error) {
      toastError(error);
    } finally {
      setClientSaving(false);
    }
  };

  const openDetailModal = async clientId => {
    try {
      setDetailLoading(true);
      setDetailOpen(true);
      const { data } = await api.get(`/clients/${clientId}`);
      setDetailRecord(data);
    } catch (error) {
      setDetailOpen(false);
      toastError(error);
    } finally {
      setDetailLoading(false);
    }
  };

  const loadResolutionHistory = async clientId => {
    const { data } = await api.get(`/clients/${clientId}/resolutions`);
    setResolutionHistory(Array.isArray(data) ? data : []);
  };

  const closeClientActionMenu = () => {
    setClientActionAnchor(null);
    setClientActionRecord(null);
  };

  const openResolutionDialog = async record => {
    closeClientActionMenu();
    try {
      setResolutionLoading(true);
      setResolutionCustomer(record);
      setResolutionFile(null);
      setSyncResolutionToNube(false);
      setNubeSyncConfirmation(null);
      setResolutionHistory([]);
      setResolutionDialogOpen(true);
      const [{ data: customer }, { data: history }] = await Promise.all([
        api.get(`/clients/${record.id}`),
        api.get(`/clients/${record.id}/resolutions`)
      ]);
      setResolutionCustomer(customer);
      setResolutionHistory(Array.isArray(history) ? history : []);
    } catch (error) {
      setResolutionDialogOpen(false);
      toastError(error);
    } finally {
      setResolutionLoading(false);
    }
  };

  const handleResolutionFileChange = async event => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      await inspectResolutionPdf(file);
      setResolutionFile(file);
    } catch (error) {
      event.target.value = "";
      setResolutionFile(null);
      toast.error(error?.message || "No fue posible validar el PDF seleccionado.");
    }
  };

  const processResolution = async () => {
    if (!resolutionCustomer?.id || !resolutionFile) {
      toast.warning("Seleccione primero un PDF oficial de resolucion DIAN.");
      return;
    }

    try {
      setResolutionProcessing(true);
      const pdfBase64 = await fileToBase64(resolutionFile);
      const { data } = await api.post(`/clients/${resolutionCustomer.id}/resolutions/process`, {
        pdf_base64: pdfBase64,
        fileName: resolutionFile.name
      });
      if (data?.pending) {
        toast.warning(data?.message || "La resolucion quedo pendiente para reintento.");
      } else {
        toast.success(data?.message || "Resolucion procesada correctamente.");
      }
      if (!data?.pending && syncResolutionToNube && data?.resolution?.id) {
        try {
          const { data: preview } = await api.post(
            `/clients/${resolutionCustomer.id}/resolutions/${data.resolution.id}/nube-sync/preview`
          );
          const status = String(preview?.status || "");
          if (["ready_to_create", "ready_to_replace"].includes(status)) {
            setNubeSyncConfirmation({ resolution: data.resolution, preview });
          } else {
            toast.info(preview?.message || "La validacion con FacilWeb Nube fue registrada.");
          }
        } catch (previewError) {
          toast.warning(previewError?.response?.data?.message || "La resolucion se proceso, pero no fue posible validar FacilWeb Nube.");
        }
      }
      setResolutionFile(null);
      await loadResolutionHistory(resolutionCustomer.id);
    } catch (error) {
      const message = error?.response?.data?.message || "No fue posible procesar la resolucion.";
      toast.error(message);
      if (resolutionCustomer?.id) {
        await loadResolutionHistory(resolutionCustomer.id).catch(() => undefined);
      }
    } finally {
      setResolutionProcessing(false);
    }
  };

  const applyNubeResolutionSync = async () => {
    if (!resolutionCustomer?.id || !nubeSyncConfirmation?.resolution?.id) return;
    try {
      setNubeSyncApplying(true);
      const { data } = await api.post(
        `/clients/${resolutionCustomer.id}/resolutions/${nubeSyncConfirmation.resolution.id}/nube-sync/apply`
      );
      toast.success(data?.message || "Resolucion sincronizada con FacilWeb Nube.");
      setNubeSyncConfirmation(null);
      await loadResolutionHistory(resolutionCustomer.id);
    } catch (error) {
      toast.error(error?.response?.data?.message || "No fue posible sincronizar la resolucion con FacilWeb Nube.");
      await loadResolutionHistory(resolutionCustomer.id).catch(() => undefined);
    } finally {
      setNubeSyncApplying(false);
    }
  };

  const retryResolution = async resolution => {
    if (!resolutionCustomer?.id) return;
    try {
      setResolutionProcessing(true);
      const { data } = await api.post(
        `/clients/${resolutionCustomer.id}/resolutions/${resolution.id}/retry`
      );
      if (data?.pending) {
        toast.warning(data?.message || "La resolucion sigue pendiente para reintento.");
      } else {
        toast.success(data?.message || "Reintento procesado correctamente.");
      }
      await loadResolutionHistory(resolutionCustomer.id);
    } catch (error) {
      toast.error(error?.response?.data?.message || "No fue posible reintentar la resolucion.");
      await loadResolutionHistory(resolutionCustomer.id).catch(() => undefined);
    } finally {
      setResolutionProcessing(false);
    }
  };

  const openResolutionDetail = async resolution => {
    if (!resolutionCustomer?.id) return;
    try {
      setResolutionDetailOpen(true);
      setResolutionDetail(null);
      const { data } = await api.get(
        `/clients/${resolutionCustomer.id}/resolutions/${resolution.id}`
      );
      setResolutionDetail(data);
    } catch (error) {
      setResolutionDetailOpen(false);
      toastError(error);
    }
  };

  const refreshApiOperations = async customerId => {
    const { data: status } = await api.get(`/clients/${customerId}/api-operations/status`);
    const { data: customer } = await api.get(`/clients/${customerId}`);
    setApiOperationsCustomer(customer);
    setApiOperationsStatus(status);
    setApiEnvironmentForm({
      invoice_environment: catalogEnvironmentValue(customer, "environment"),
      payroll_environment: catalogEnvironmentValue(customer, "payroll_environment"),
      eqdocs_environment: catalogEnvironmentValue(customer, "eqdocs_environment")
    });

    try {
      const { data: plans } = await api.get(`/clients/${customerId}/api-operations/plans`);
      setApiOperationsPlans(plans);
    } catch (error) {
      setApiOperationsPlans({
        error: error?.response?.data?.message || "No fue posible consultar planes para este cliente."
      });
    }
  };

  const openApiOperationsDialog = async record => {
    closeClientActionMenu();
    try {
      setApiOperationsOpen(true);
      setApiOperationsLoading(true);
      setApiOperationsCustomer(record);
      setApiOperationsStatus(null);
      setApiOperationsPlans(null);
      setApiOperationsTab(0);
      setApiPlanForm(emptyApiPlanForm);
      await refreshApiOperations(record.id);
    } catch (error) {
      setApiOperationsOpen(false);
      toast.error(error?.response?.data?.message || "No fue posible consultar ambientes y planes del cliente.");
    } finally {
      setApiOperationsLoading(false);
    }
  };

  const handleSaveApiEnvironments = async () => {
    if (!apiOperationsCustomer?.id) return;
    const hasChange = Object.values(apiEnvironmentForm).some(Boolean);
    if (!hasChange) {
      toast.warning("Seleccione al menos un ambiente para enviar a la API.");
      return;
    }

    const confirmed = window.confirm("Este cambio actualizara ambientes en la API del cliente. Desea continuar?");
    if (!confirmed) return;

    try {
      setApiOperationsSaving(true);
      const { data } = await api.put(`/clients/${apiOperationsCustomer.id}/api-operations/environments`, {
        confirm: true,
        ...apiEnvironmentForm
      });
      toast.success(data?.message || "Ambientes actualizados.");
      await refreshApiOperations(apiOperationsCustomer.id);
      await loadRecords();
    } catch (error) {
      toast.error(error?.response?.data?.message || "No fue posible actualizar ambientes.");
    } finally {
      setApiOperationsSaving(false);
    }
  };

  const handleAssignApiPlan = async () => {
    if (!apiOperationsCustomer?.id) return;
    if (!apiPlanForm.service) {
      toast.warning("Seleccione el servicio del plan.");
      return;
    }
    if (!apiPlanForm.plan_id && !apiPlanForm.absolut_plan_documents) {
      toast.warning("Indique un plan_id o folios absolutos.");
      return;
    }

    const confirmed = window.confirm("Esta accion asignara plan o folios en la API del cliente. Desea continuar?");
    if (!confirmed) return;

    try {
      setApiOperationsSaving(true);
      const { data } = await api.post(`/clients/${apiOperationsCustomer.id}/api-operations/plans/assign`, {
        confirm: true,
        service: apiPlanForm.service,
        plan_id: apiPlanForm.plan_id ? Number(apiPlanForm.plan_id) : undefined,
        absolut_plan_documents: apiPlanForm.absolut_plan_documents
          ? Number(apiPlanForm.absolut_plan_documents)
          : undefined,
        renew: apiPlanForm.renew,
        notes: apiPlanForm.notes
      });
      toast.success(data?.message || "Plan o folios asignados.");
      setApiPlanForm(emptyApiPlanForm);
      await refreshApiOperations(apiOperationsCustomer.id);
    } catch (error) {
      toast.error(error?.response?.data?.message || "No fue posible asignar plan o folios.");
    } finally {
      setApiOperationsSaving(false);
    }
  };

  const handleSyncAllApiOperations = async () => {
    try {
      setSyncAllApiLoading(true);
      const { data: preview } = await api.post("/clients/api-operations/sync-all", { dryRun: true });
      const confirmed = window.confirm(
        `Revision terminada: ${preview.matchedCustomers || 0} de ${preview.totalCustomers || 0} clientes aparecen en APIs activas. ` +
        "Si confirma, SoluWats copiara desde la API el ambiente actual de FE, nomina y documento soporte, usando el registro mas reciente si el cliente aparece en varias APIs. No cambia nada en las APIs. Desea aplicar?"
      );
      if (!confirmed) {
        toast.info("Revision realizada sin guardar cambios.");
        return;
      }

      const { data } = await api.post("/clients/api-operations/sync-all", { dryRun: false });
      toast.success(data?.message || "Sincronizacion aplicada.");
      await loadRecords();
    } catch (error) {
      toast.error(error?.response?.data?.message || "No fue posible sincronizar estados desde las APIs.");
    } finally {
      setSyncAllApiLoading(false);
    }
  };

  const openCreateEstablishmentModal = () => {
    setEstablishmentForm(emptyEstablishmentForm);
    setEstablishmentModalOpen(true);
  };

  const openEditEstablishmentModal = establishment => {
    setEstablishmentForm({
      id: establishment.id,
      installationCode: establishment.installationCode || "",
      name: establishment.name || "",
      address: establishment.address || "",
      departmentCode: establishment.departmentCode || "",
      departmentName: establishment.departmentName || "",
      municipalityCode: establishment.municipalityCode || "",
      municipalityName: establishment.municipalityName || "",
      primaryPhone: establishment.primaryPhone || "",
      primaryEmail: establishment.primaryEmail || "",
      notes: establishment.notes || "",
      isActive: establishment.isActive !== false
    });
    setEstablishmentModalOpen(true);
  };

  const handleSaveEstablishment = async () => {
    try {
      if (!detailRecord?.id) {
        return;
      }

      const payload = {
        ...establishmentForm,
        name: String(establishmentForm.name || "").trim()
      };

      if (!payload.name) {
        toast.error("El nombre del establecimiento es obligatorio.");
        return;
      }

      setEstablishmentSaving(true);

      if (establishmentForm.id) {
        await api.put(`/clients/${detailRecord.id}/establishments/${establishmentForm.id}`, payload);
        toast.success("Establecimiento actualizado.");
      } else {
        await api.post(`/clients/${detailRecord.id}/establishments`, payload);
        toast.success("Establecimiento creado.");
      }

      setEstablishmentModalOpen(false);
      await reloadDetailRecord(detailRecord.id);
      await loadRecords(true);
    } catch (error) {
      toastError(error);
    } finally {
      setEstablishmentSaving(false);
    }
  };

  const departmentOptions = useMemo(
    () =>
      [...(geoCatalogs.departments || [])].sort((left, right) =>
        String(left.name || "").localeCompare(String(right.name || ""), "es", {
          sensitivity: "base",
          numeric: true
        })
      ),
    [geoCatalogs.departments]
  );

  const municipalityOptions = useMemo(() => {
    const selectedDepartmentCode = String(establishmentForm.departmentCode || "").trim();
    const rows = [...(geoCatalogs.municipalities || [])];

    const filtered = selectedDepartmentCode
      ? rows.filter(item => String(item.departmentCode || "") === selectedDepartmentCode)
      : rows;

    return filtered.sort((left, right) =>
      String(left.name || "").localeCompare(String(right.name || ""), "es", {
        sensitivity: "base",
        numeric: true
      })
    );
  }, [establishmentForm.departmentCode, geoCatalogs.municipalities]);

  const selectedDepartment = useMemo(
    () => departmentOptions.find(item => String(item.code) === String(establishmentForm.departmentCode || "")) || null,
    [departmentOptions, establishmentForm.departmentCode]
  );

  const selectedMunicipality = useMemo(
    () => municipalityOptions.find(item => String(item.code) === String(establishmentForm.municipalityCode || "")) || null,
    [municipalityOptions, establishmentForm.municipalityCode]
  );

  const getAttributeOptions = catalogType =>
    Array.isArray(catalogs.attributes?.[catalogType]) ? catalogs.attributes[catalogType] : [];

  const handleEditableCatalogChange = (catalogType, selectedCode) => {
    const options = getAttributeOptions(catalogType);
    const selected = options.find(
      option => String(option.code || option.name || "") === String(selectedCode || "")
    );

    setClientForm(prev => ({
      ...prev,
      catalogSelections: {
        ...(prev.catalogSelections || {}),
        [catalogType]: selected
          ? { code: selected.code || "", name: selected.name || selected.code || "" }
          : null
      }
    }));
  };

  const handleLinkPossibleContacts = async () => {
    try {
      if (!detailRecord?.id) {
        return;
      }

      const { data } = await api.post(`/clients/${detailRecord.id}/link-contacts`);
      toast.success(
        `Contactos vinculados: ${Number(data?.linkedContacts || 0)}. Tickets: ${Number(data?.linkedTickets || 0)}. Tareas: ${Number(data?.linkedTasks || 0)}.`
      );
      await reloadDetailRecord(detailRecord.id);
      await loadRecords();
    } catch (error) {
      toastError(error);
    }
  };

  const handleManualAssignContacts = async () => {
    try {
      if (!detailRecord?.id || !contactLinkSelection.length) {
        toast.warning("Seleccione al menos un contacto.");
        return;
      }

      const { data } = await api.post(`/clients/${detailRecord.id}/assign-contacts`, {
        contactIds: contactLinkSelection.map(item => item.id)
      });

      toast.success(
        `Contactos relacionados: ${Number(data?.linkedContacts || 0)}. Tickets: ${Number(data?.linkedTickets || 0)}. Tareas: ${Number(data?.linkedTasks || 0)}.`
      );
      setContactLinkDialog(false);
      setContactLinkSelection([]);
      await reloadDetailRecord(detailRecord.id);
      await loadRecords();
    } catch (error) {
      toastError(error);
    }
  };

  const handleDeleteEstablishment = async establishment => {
    try {
      if (!detailRecord?.id || !establishment?.id) {
        return;
      }

      const confirmed = window.confirm(
        `Va a eliminar el establecimiento "${establishment.name || "sin nombre"}". Esta accion no se puede deshacer.`
      );

      if (!confirmed) {
        return;
      }

      await api.delete(`/clients/${detailRecord.id}/establishments/${establishment.id}`);
      toast.success("Establecimiento eliminado.");
      await reloadDetailRecord(detailRecord.id);
      await loadRecords();
    } catch (error) {
      toastError(error);
    }
  };

  const openContactLinkDialog = async () => {
    try {
      if (!detailRecord?.id) {
        return;
      }

      setContactLinkLoading(true);
      setContactLinkDialog(true);

      const { data } = await api.get("/contacts/list");
      const relatedIds = new Set((detailRecord.contacts || []).map(item => Number(item.id)));

      const options = (Array.isArray(data) ? data : [])
        .filter(item => !item?.isGroup)
        .filter(item => Number(item?.businessCustomerId || 0) !== Number(detailRecord.id))
        .filter(item => {
          const number = String(item?.number || "").trim();
          const name = String(item?.name || "").trim();

          if (hasUsableClientContactName(item)) return true;
          if (!number) return false;
          if (!/^\d+$/.test(number)) return true;

          return number.length <= 15 && name !== number;
        })
        .filter(item => !relatedIds.has(Number(item.id)))
        .sort((left, right) =>
          buildClientContactLabel(left).localeCompare(buildClientContactLabel(right), "es", {
            sensitivity: "base",
            numeric: true
          })
        );

      setContactLinkOptions(options);
    } catch (error) {
      setContactLinkDialog(false);
      toastError(error);
    } finally {
      setContactLinkLoading(false);
    }
  };

  const openRutOnboarding = async () => {
    try {
      setRutOnboardingOpen(true);
      const savedDraft = readRutOnboardingDraft();
      setRutOnboardingForm(savedDraft ? { ...emptyRutOnboardingForm, ...savedDraft } : emptyRutOnboardingForm);
      setRutOnboardingDraftRestored(Boolean(savedDraft));
      setRutOnboardingResult(null);
      const { data } = await api.get("/clients/sources/alta-rut");
      const availableSources = Array.isArray(data) ? data : [];
      setRutSources(availableSources);
      const firstEnabled = availableSources.find(source => source.enabledForRutOnboarding);
      if (firstEnabled) {
        setRutOnboardingForm(prev => ({ ...prev, sourceId: String(firstEnabled.id) }));
      }
    } catch (error) {
      setRutOnboardingOpen(false);
      toastError(error);
    }
  };

  const updateRutFile = async (field, file) => {
    if (!file) return;
    if (field === "resolutionFiles") {
      try {
        const inspectedFile = await inspectResolutionPdf(file);
        setRutOnboardingForm(prev => {
          const exists = prev.resolutionFiles.some(item => item.file.name === file.name && item.file.size === file.size && item.file.lastModified === file.lastModified);
          if (exists) return prev;
          return { ...prev, resolutionFiles: [...prev.resolutionFiles, inspectedFile] };
        });
        toast.success(`Resolucion validada: ${file.name}`);
      } catch (error) {
        toast.error(error.message || "No fue posible validar la resolucion.");
      }
      return;
    }
    if (field === "rutFile" && (!/\.pdf$/i.test(file.name) || file.size > 8 * 1024 * 1024)) {
      toast.warning("El RUT debe ser un PDF de maximo 8 MB.");
      return;
    }
    if (field === "logoFile" && (!/^image\/(png|jpeg)$/.test(file.type) || file.size > 2 * 1024 * 1024)) {
      toast.warning("El logo debe ser JPG o PNG de maximo 2 MB.");
      return;
    }
    setRutOnboardingForm(prev => ({ ...prev, [field]: file }));
  };

  const removeResolutionFile = index => {
    setRutOnboardingForm(prev => ({
      ...prev,
      resolutionFiles: prev.resolutionFiles.filter((_, itemIndex) => itemIndex !== index)
    }));
  };

  const clearRutOnboarding = () => {
    clearRutOnboardingDraft();
    setRutOnboardingForm(emptyRutOnboardingForm);
    setRutOnboardingResult(null);
    setRutOnboardingDraftRestored(false);
    toast.info("Formulario limpiado.");
  };

  const parseOptionalJson = (value, label) => {
    if (!String(value || "").trim()) return undefined;
    try {
      return JSON.parse(value);
    } catch (_error) {
      throw new Error(`${label} debe ser JSON valido.`);
    }
  };

  const submitRutOnboarding = async createMode => {
    try {
      const selectedSource = rutSources.find(source => String(source.id) === String(rutOnboardingForm.sourceId));
      if (!selectedSource?.enabledForRutOnboarding) {
        toast.warning("Seleccione una API habilitada para altas desde RUT.");
        return;
      }
      if (!rutOnboardingForm.rutFile) {
        toast.warning("Adjunte primero el PDF del RUT.");
        return;
      }
      if (createMode && !rutOnboardingForm.confirmCreate) {
        toast.warning("Confirme que desea crear o completar la empresa en la API seleccionada.");
        return;
      }
      if (createMode && !String(rutOnboardingForm.softwareId || "").trim()) {
        toast.warning("Pegue el Software ID de DIAN antes de crear la empresa.");
        return;
      }
      if (createMode && !String(rutOnboardingForm.softwareClassificationCode || "").trim()) {
        toast.warning("Seleccione la clasificación de software antes de crear.");
        return;
      }
      if (createMode && !String(rutOnboardingForm.commercialClassificationCode || "").trim()) {
        toast.warning("Seleccione la clasificación comercial antes de crear.");
        return;
      }
      if (rutOnboardingForm.runInvoiceTests && !String(rutOnboardingForm.invoiceTestSetId || "").trim()) {
        toast.warning("Para ejecutar pruebas de factura indique el Set de pruebas DIAN.");
        return;
      }

      setRutOnboardingSaving(true);
      const [pdfBase64, certificateBase64, logoBase64] = await Promise.all([
        fileToBase64(rutOnboardingForm.rutFile),
        rutOnboardingForm.certificateFile ? fileToBase64(rutOnboardingForm.certificateFile) : Promise.resolve(undefined),
        rutOnboardingForm.logoFile ? fileToBase64(rutOnboardingForm.logoFile) : Promise.resolve(undefined)
      ]);

      const payload = {
        pdf_base64: pdfBase64,
        dry_run: !createMode,
        create: createMode,
        update_existing: createMode && rutOnboardingForm.updateExisting,
        complete_existing_if_not_production: createMode && rutOnboardingForm.completeExisting,
        services: { invoice: rutOnboardingForm.invoice, payroll: rutOnboardingForm.payroll, eqdocs: rutOnboardingForm.eqdocs },
        local_software_classification: rutOnboardingForm.softwareClassificationCode,
        local_commercial_classification: rutOnboardingForm.commercialClassificationCode,
        software_name: rutOnboardingForm.softwareName || undefined,
        software_id: rutOnboardingForm.softwareId.trim() || undefined,
        software_pin: rutOnboardingForm.softwarePin.trim() || "12345",
        certificate_base64: certificateBase64,
        certificate_password: rutOnboardingForm.certificatePassword || undefined,
        logo_base64: logoBase64,
        setup_environment: rutOnboardingForm.setupEnvironment,
        type_environment_id: 2,
        payroll_type_environment_id: 2,
        eqdocs_type_environment_id: 2,
        setup_test_resolutions: rutOnboardingForm.setupTestResolutions,
        run_invoice_tests: createMode && rutOnboardingForm.runInvoiceTests,
        run_payroll_tests: createMode && rutOnboardingForm.runPayrollTests,
        invoice_test_set_id: rutOnboardingForm.invoiceTestSetId || undefined,
        payroll_test_set_id: rutOnboardingForm.payrollTestSetId || undefined,
        invoice_test_start_number: rutOnboardingForm.invoiceTestStartNumber ? Number(rutOnboardingForm.invoiceTestStartNumber) : undefined,
        credit_note_test_number: rutOnboardingForm.creditNoteTestNumber ? Number(rutOnboardingForm.creditNoteTestNumber) : undefined,
        debit_note_test_number: rutOnboardingForm.debitNoteTestNumber ? Number(rutOnboardingForm.debitNoteTestNumber) : undefined,
        overrides: parseOptionalJson(rutOnboardingForm.overridesJson, "Correcciones manuales"),
        resolutions: parseOptionalJson(rutOnboardingForm.resolutionsJson, "Resoluciones personalizadas")
      };

      const { data } = await api.post(`/clients/sources/${selectedSource.id}/alta-rut`, payload);
      const result = data?.result || data;
      setRutOnboardingResult(result);
      const identificationNumber = String(
        result?.company_payload?.identification_number
        || result?.company?.identification_number
        || result?.identification_number
        || ""
      ).replace(/\D/g, "");
      if (identificationNumber) {
        setRutOnboardingForm(prev => ({
          ...prev,
          softwareName: `facilweb_${identificationNumber}`
        }));
      }
      if (createMode) {
        clearRutOnboardingDraft();
        setRutOnboardingDraftRestored(false);
      }
      toast.success(createMode ? "La API proceso el alta de la empresa." : "RUT validado. Revise el resultado antes de crear.");
      if (createMode) await loadRecords();
    } catch (error) {
      persistRutOnboardingDraft(rutOnboardingForm);
      setRutOnboardingDraftRestored(true);
      const rutErrorResult = error?.response?.data?.result || error?.response?.data || null;
      setRutOnboardingResult(rutErrorResult);
      const missingFields = formatRutMissingFields(rutErrorResult);
      if (missingFields) {
        toast.error(`El RUT fue leido, pero faltan datos para crear: ${missingFields}. Use correcciones manuales JSON o revise el PDF.`);
        return;
      }
      if (error?.message && !error?.response) toast.error(error.message);
      else toastError(error);
    } finally {
      setRutOnboardingSaving(false);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Clientes</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ReplayIcon />}
            onClick={() => loadRecords()}
            disabled={loading}
          >
            Actualizar
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => handleExport("xlsx")}
            disabled={exporting}
          >
            Exportar Excel
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => handleExport("pdf")}
            disabled={exporting}
          >
            Exportar PDF
          </Button>
          {isAdmin ? (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ReplayIcon />}
              onClick={handleSyncAllApiOperations}
              disabled={syncAllApiLoading}
            >
              {syncAllApiLoading ? "Sincronizando..." : "Traer ambientes APIs"}
            </Button>
          ) : null}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openCreateClientModal}
          >
            Nuevo cliente
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={openRutOnboarding}
          >
            Dar de alta por RUT
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.hero}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="overline" style={{ fontWeight: 700, letterSpacing: "0.14em" }}>
              Panel comercial operativo
            </Typography>
            <Typography variant="h3" style={{ fontWeight: 800, marginTop: 8 }}>
              Clientes
            </Typography>
            <Typography variant="body1" style={{ maxWidth: 700, marginTop: 12, lineHeight: 1.7 }}>
              Este modulo centraliza clientes, relaciones operativas y futuras integraciones con
              contactos, tickets, tareas, resoluciones y automatizaciones.
            </Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper className={classes.metricCard}>
              <Typography variant="overline">Registros visibles</Typography>
              <Typography variant="h3" style={{ fontWeight: 800, marginTop: 8 }}>
                {count}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Resultado filtrado actual.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper className={classes.metricCard}>
              <Typography variant="overline">Activos / Manuales</Typography>
              <Typography variant="h3" style={{ fontWeight: 800, marginTop: 8 }}>
                {activeCount}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {manualCount} creados manualmente.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      <Paper className={classes.filterCard}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              variant="outlined"
              label="Buscar cliente"
              value={searchParam}
              onChange={e => setSearchParam(e.target.value)}
              placeholder="Nombre, NIT, correo, telefono, direccion..."
              InputProps={{
                startAdornment: <SearchIcon style={{ marginRight: 8, opacity: 0.7 }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Software</InputLabel>
              <Select
                label="Software"
                value={softwareClassification}
                onChange={e => setSoftwareClassification(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="__none__">Sin software</MenuItem>
                {catalogs.software.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Clasificacion comercial</InputLabel>
              <Select
                label="Clasificacion comercial"
                value={commercialClassification}
                onChange={e => setCommercialClassification(e.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="__none__">Sin clasificación comercial</MenuItem>
                {catalogs.commercial.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Estado</InputLabel>
              <Select
                label="Estado"
                value={activeFilter}
                onChange={e => setActiveFilter(e.target.value)}
              >
                <MenuItem value="active">Activos</MenuItem>
                <MenuItem value="inactive">Inactivos</MenuItem>
                <MenuItem value="all">Todos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Ordenar por</InputLabel>
              <Select
                label="Ordenar por"
                value={`${sortBy}:${sortDirection}`}
                onChange={e => {
                  const [nextSortBy, nextDirection] = String(e.target.value).split(":");
                  setSortBy(nextSortBy);
                  setSortDirection(nextDirection);
                }}
              >
                <MenuItem value="createdAt:DESC">Más recientes primero</MenuItem>
                <MenuItem value="createdAt:ASC">Más antiguos primero</MenuItem>
                <MenuItem value="name:ASC">Nombre A-Z</MenuItem>
                <MenuItem value="name:DESC">Nombre Z-A</MenuItem>
                <MenuItem value="software:ASC">Software A-Z</MenuItem>
                <MenuItem value="commercial:ASC">Clasificación comercial A-Z</MenuItem>
                <MenuItem value="status:DESC">Estado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={1}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setSearchParam("");
                setSoftwareClassification("");
                setCommercialClassification("");
                setActiveFilter("active");
                setSortBy("createdAt");
                setSortDirection("DESC");
                setPageNumber(1);
              }}
            >
              Limpiar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper className={classes.tableCard}>
        <div className={classes.tableWrap}>
          <Table>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={classes.th}>ID</TableCell>
                <TableCell className={classes.th}>Cliente</TableCell>
                <TableCell className={classes.th}>Clasificacion</TableCell>
                <TableCell className={classes.th}>Contacto</TableCell>
                <TableCell className={classes.th}>Estado</TableCell>
                <TableCell className={classes.th}>Origen</TableCell>
                <TableCell className={classes.th}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map(record => (
                <TableRow key={record.id} className={classes.row}>
                  <TableCell className={classes.idCell}>#{record.id}</TableCell>
                  <TableCell>
                    <div className={classes.nameBlock}>
                      <Typography variant="subtitle2" style={{ fontWeight: 800 }}>
                        {record.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {record.identificationNumber}{record.dv ? `-${record.dv}` : ""}
                      </Typography>
                      {record.integrationSource?.name && (
                        <Typography variant="caption" color="textSecondary">
                          Fuente: {record.integrationSource.name}
                        </Typography>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column" gridGap={6}>
                      {record.softwareClassificationCode ? (
                        <Chip size="small" label={record.softwareClassificationCode} />
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          Sin software
                        </Typography>
                      )}
                      {record.commercialClassificationCode ? (
                        <Chip size="small" label={record.commercialClassificationCode} color="primary" />
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          Sin clasificacion comercial
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column" gridGap={4}>
                      <Typography variant="body2">{record.email || "Sin correo"}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {record.phone || record.primaryNotificationPhone || "Sin telefono"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={record.isActive ? "Activo" : "Inactivo"}
                      style={metricStyle(record.isActive)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={record.originCode || "manual"} />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      aria-label="Mas acciones del cliente"
                      onClick={event => {
                        setClientActionAnchor(event.currentTarget);
                        setClientActionRecord(record);
                      }}
                      style={{ marginLeft: 4 }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <TablePagination
          component="div"
          count={count}
          page={Math.max(pageNumber - 1, 0)}
          onPageChange={(_event, nextPage) => setPageNumber(nextPage + 1)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={event => {
            const nextValue = Number(event.target.value);
            setPageSize(nextValue);
            setPageNumber(1);
          }}
          rowsPerPageOptions={[
            20,
            30,
            50,
            100,
            200,
            300,
            500,
            1000,
            2000,
            { label: "Todos", value: -1 }
          ]}
          labelRowsPerPage="Mostrar"
          labelDisplayedRows={({ from, to, count: total }) =>
            `${from}-${to} de ${total === -1 ? to : total}`
          }
        />
      </Paper>

      <Menu
        anchorEl={clientActionAnchor}
        open={Boolean(clientActionAnchor)}
        onClose={closeClientActionMenu}
        keepMounted
      >
        <MenuItem onClick={() => {
          const record = clientActionRecord;
          closeClientActionMenu();
          if (record) openDetailModal(record.id);
        }}>
          <VisibilityIcon fontSize="small" style={{ marginRight: 10 }} />
          Ver
        </MenuItem>
        <MenuItem onClick={() => {
          const record = clientActionRecord;
          closeClientActionMenu();
          if (record) openEditClientModal(record);
        }}>
          <EditIcon fontSize="small" style={{ marginRight: 10 }} />
          Editar
        </MenuItem>
        <MenuItem onClick={() => clientActionRecord && openResolutionDialog(clientActionRecord)}>
          <DescriptionIcon fontSize="small" style={{ marginRight: 10 }} />
          Subir resolucion DIAN
        </MenuItem>
        <MenuItem onClick={() => clientActionRecord && openApiOperationsDialog(clientActionRecord)}>
          <ReplayIcon fontSize="small" style={{ marginRight: 10 }} />
          Ambiente real y planes
        </MenuItem>
      </Menu>

      <Dialog
        open={rutOnboardingOpen}
        onClose={() => !rutOnboardingSaving && setRutOnboardingOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>Dar de alta empresa desde RUT</DialogTitle>
        <DialogContent dividers>
          <Box mb={2} p={1.5} borderRadius={12} style={{ background: "rgba(28,78,125,0.08)" }}>
            <Typography variant="subtitle2" style={{ fontWeight: 800 }}>Flujo seguro de alta</Typography>
            <Typography variant="body2" color="textSecondary">
              Primero valide el PDF de RUT. La creacion solo se habilita con confirmacion explicita y la API destino seleccionada.
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>API destino</InputLabel>
                <Select
                  label="API destino"
                  value={rutOnboardingForm.sourceId}
                  onChange={e => setRutOnboardingForm(prev => ({ ...prev, sourceId: e.target.value }))}
                >
                  {rutSources.map(source => (
                    <MenuItem key={source.id} value={String(source.id)} disabled={!source.enabledForRutOnboarding}>
                      {source.name} {!source.enabledForRutOnboarding ? "(alta no habilitada)" : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                type="file"
                label="PDF del RUT DIAN"
                InputLabelProps={{ shrink: true }}
                inputProps={{ accept: "application/pdf,.pdf" }}
                onChange={e => updateRutFile("rutFile", e.target.files?.[0])}
                helperText={rutOnboardingForm.rutFile ? rutOnboardingForm.rutFile.name : "PDF legible, sin clave, maximo 8 MB."}
              />
            </Grid>
            {rutOnboardingDraftRestored && <Grid item xs={12}>
              <Paper variant="outlined" style={{ padding: 10, borderRadius: 10, borderColor: "#d69e2e" }}>
                <Typography variant="body2"><strong>Se restauró el último intento con error.</strong> Revise y corrija los datos necesarios. Por seguridad del navegador, debe adjuntar nuevamente RUT, certificado, logo y resoluciones.</Typography>
              </Paper>
            </Grid>}
            <Grid item xs={12}>
              <Typography variant="subtitle2" style={{ fontWeight: 800 }}>Servicios a configurar</Typography>
              <FormControlLabel control={<Switch checked={rutOnboardingForm.invoice} onChange={e => setRutOnboardingForm(prev => ({ ...prev, invoice: e.target.checked }))} color="primary" />} label="Facturacion electronica" />
              <FormControlLabel control={<Switch checked={rutOnboardingForm.payroll} onChange={e => setRutOnboardingForm(prev => ({ ...prev, payroll: e.target.checked }))} color="primary" />} label="Nomina electronica" />
              <FormControlLabel control={<Switch checked={rutOnboardingForm.eqdocs} onChange={e => setRutOnboardingForm(prev => ({ ...prev, eqdocs: e.target.checked }))} color="primary" />} label="Documento soporte / equivalentes" />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Clasificación de software</InputLabel>
                <Select
                  label="Clasificación de software"
                  value={rutOnboardingForm.softwareClassificationCode}
                  onChange={e => setRutOnboardingForm(prev => ({ ...prev, softwareClassificationCode: e.target.value }))}
                >
                  <MenuItem value="">Seleccionar...</MenuItem>
                  {catalogs.software.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Clasificación comercial</InputLabel>
                <Select
                  label="Clasificación comercial"
                  value={rutOnboardingForm.commercialClassificationCode}
                  onChange={e => setRutOnboardingForm(prev => ({ ...prev, commercialClassificationCode: e.target.value }))}
                >
                  <MenuItem value="">Seleccionar...</MenuItem>
                  {catalogs.commercial.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" style={{ padding: 12, borderRadius: 12 }}>
                <Typography variant="subtitle2" style={{ fontWeight: 800, marginBottom: 10 }}>Software DIAN</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={<RutHelpLabel label="Nombre visible del software" help="SOLUWHATS lo calcula tras validar el RUT: facilweb_ seguido del NIT sin dígito de verificación." />}
                      value={rutOnboardingForm.softwareName}
                      InputProps={{ readOnly: true }}
                      helperText="Se calcula como facilweb_ + NIT sin DV al validar el RUT."
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      required
                      variant="outlined"
                      label={<RutHelpLabel label="Software ID DIAN" help="Pegue el UUID de la columna Id de DIAN. No es el nombre visible del software." />}
                      value={rutOnboardingForm.softwareId}
                      onChange={e => setRutOnboardingForm(prev => ({ ...prev, softwareId: e.target.value.trim() }))}
                      placeholder="UUID de la columna Id de DIAN"
                      helperText="Pegue el UUID tecnico mostrado por DIAN. No es el nombre del software."
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={<RutHelpLabel label="PIN del software" help="Clave corta configurada para el software en DIAN. El valor inicial recomendado es 12345." />}
                      value={rutOnboardingForm.softwarePin}
                      onChange={e => setRutOnboardingForm(prev => ({ ...prev, softwarePin: e.target.value }))}
                      helperText="Por defecto: 12345"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth variant="outlined" type="file" label="Certificado digital (opcional)" InputLabelProps={{ shrink: true }} inputProps={{ accept: ".p12,.pfx,application/x-pkcs12" }} onChange={e => updateRutFile("certificateFile", e.target.files?.[0])} helperText={rutOnboardingForm.certificateFile ? rutOnboardingForm.certificateFile.name : "P12 o PFX"} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth variant="outlined" type="password" label="Clave del certificado" value={rutOnboardingForm.certificatePassword} onChange={e => setRutOnboardingForm(prev => ({ ...prev, certificatePassword: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth variant="outlined" type="file" label="Logo (opcional)" InputLabelProps={{ shrink: true }} inputProps={{ accept: "image/png,image/jpeg" }} onChange={e => updateRutFile("logoFile", e.target.files?.[0])} helperText={rutOnboardingForm.logoFile ? rutOnboardingForm.logoFile.name : "PNG o JPG, maximo 2 MB"} />
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" style={{ padding: 12, borderRadius: 12 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={8}>
                  <Box>
                    <Typography variant="subtitle2" style={{ fontWeight: 800 }}>Resoluciones DIAN (PDF)</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Adjunte factura electronica, documento soporte u otras resoluciones. Solo PDF DIAN legible, sin clave ni imagen escaneada.
                    </Typography>
                  </Box>
                  <Button component="label" size="small" color="primary" variant="outlined" startIcon={<AddIcon />}>
                    Adjuntar PDF
                    <input hidden type="file" accept="application/pdf,.pdf" onChange={e => {
                      const files = Array.from(e.target.files || []);
                      files.forEach(file => updateRutFile("resolutionFiles", file));
                      e.target.value = "";
                    }} multiple />
                  </Button>
                </Box>
                {rutOnboardingForm.resolutionFiles.length > 0 && (
                  <Box mt={1} display="flex" flexWrap="wrap" gridGap={8}>
                    {rutOnboardingForm.resolutionFiles.map((item, index) => (
                      <Chip
                        key={`${item.file.name}-${item.file.lastModified}`}
                        label={`${item.file.name} (${item.pages} pag.)`}
                        onDelete={() => removeResolutionFile(index)}
                        deleteIcon={<DeleteOutlineIcon />}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                  El endpoint actual recibe los datos estructurados de cada resolucion. Estos PDF quedan validados y listos para adjuntar cuando la API publique su campo de recepcion.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Button
                size="small"
                color="primary"
                variant="outlined"
                onClick={() => setRutOnboardingForm(prev => ({ ...prev, showAdvanced: !prev.showAdvanced }))}
              >
                {rutOnboardingForm.showAdvanced ? "Ocultar opciones tecnicas" : "Mostrar opciones tecnicas y pruebas"}
              </Button>
            </Grid>
            {rutOnboardingForm.showAdvanced && <>
            <Grid item xs={12} md={6}>
              <FormControlLabel control={<Switch checked={rutOnboardingForm.setupEnvironment} onChange={e => setRutOnboardingForm(prev => ({ ...prev, setupEnvironment: e.target.checked }))} color="primary" />} label="Preparar ambiente de habilitacion" />
              <FormControlLabel control={<Switch checked={rutOnboardingForm.setupTestResolutions} onChange={e => setRutOnboardingForm(prev => ({ ...prev, setupTestResolutions: e.target.checked }))} color="primary" />} label="Crear resoluciones estandar de prueba" />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" style={{ fontWeight: 800 }}>Pruebas DIAN (solo bajo solicitud expresa)</Typography>
              <FormControlLabel control={<Switch checked={rutOnboardingForm.runInvoiceTests} onChange={e => setRutOnboardingForm(prev => ({ ...prev, runInvoiceTests: e.target.checked }))} color="primary" />} label="Ejecutar pruebas de factura" />
              <FormControlLabel control={<Switch checked={rutOnboardingForm.runPayrollTests} onChange={e => setRutOnboardingForm(prev => ({ ...prev, runPayrollTests: e.target.checked }))} color="primary" />} label="Ejecutar pruebas de nomina" />
            </Grid>
            {rutOnboardingForm.runInvoiceTests && <>
              <Grid item xs={12} md={6}><TextField required fullWidth variant="outlined" label={<RutHelpLabel label="Set de pruebas factura DIAN" help="UUID que DIAN entrega para ejecutar el set de pruebas de factura de esta empresa." />} value={rutOnboardingForm.invoiceTestSetId} onChange={e => setRutOnboardingForm(prev => ({ ...prev, invoiceTestSetId: e.target.value }))} helperText="Obligatorio para ejecutar las pruebas de factura." /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth type="number" variant="outlined" label="Consecutivo factura" value={rutOnboardingForm.invoiceTestStartNumber} onChange={e => setRutOnboardingForm(prev => ({ ...prev, invoiceTestStartNumber: e.target.value }))} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth type="number" variant="outlined" label="Consecutivo NC" value={rutOnboardingForm.creditNoteTestNumber} onChange={e => setRutOnboardingForm(prev => ({ ...prev, creditNoteTestNumber: e.target.value }))} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth type="number" variant="outlined" label="Consecutivo ND" value={rutOnboardingForm.debitNoteTestNumber} onChange={e => setRutOnboardingForm(prev => ({ ...prev, debitNoteTestNumber: e.target.value }))} /></Grid>
            </>}
            {rutOnboardingForm.runPayrollTests && <Grid item xs={12} md={6}><TextField fullWidth variant="outlined" label="Set de pruebas nomina DIAN" value={rutOnboardingForm.payrollTestSetId} onChange={e => setRutOnboardingForm(prev => ({ ...prev, payrollTestSetId: e.target.value }))} /></Grid>}
            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={rutOnboardingForm.completeExisting} onChange={e => setRutOnboardingForm(prev => ({ ...prev, completeExisting: e.target.checked }))} color="primary" />} label="Completar empresa existente solo si sigue en habilitacion" />
              <FormControlLabel control={<Switch checked={rutOnboardingForm.updateExisting} onChange={e => setRutOnboardingForm(prev => ({ ...prev, updateExisting: e.target.checked }))} color="primary" />} label="Permitir actualizar empresa existente no productiva" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth multiline minRows={3} variant="outlined" label="Correcciones manuales JSON (opcional)" value={rutOnboardingForm.overridesJson} onChange={e => setRutOnboardingForm(prev => ({ ...prev, overridesJson: e.target.value }))} placeholder={'{"email":"correo@empresa.com","phone":"3000000000"}'} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth multiline minRows={3} variant="outlined" label="Resoluciones personalizadas JSON (opcional)" value={rutOnboardingForm.resolutionsJson} onChange={e => setRutOnboardingForm(prev => ({ ...prev, resolutionsJson: e.target.value }))} placeholder={'[{"type_document_id":1,"prefix":"FV","resolution":"..."}]'} />
            </Grid>
            </>}
            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={rutOnboardingForm.confirmCreate} onChange={e => setRutOnboardingForm(prev => ({ ...prev, confirmCreate: e.target.checked }))} color="secondary" />} label="Confirmo que deseo crear o completar esta empresa en la API seleccionada." />
            </Grid>
            {rutOnboardingResult && (
              <Grid item xs={12}>
                <Paper variant="outlined" style={{ padding: 16, maxHeight: 310, overflow: "auto" }}>
                  <Typography variant="subtitle2" style={{ fontWeight: 800, marginBottom: 8 }}>Resultado de la API</Typography>
                  {getRutMissingFields(rutOnboardingResult).length > 0 && (
                    <Box mb={1.5} p={1.5} borderRadius={12} bgcolor="rgba(244, 67, 54, 0.10)" border="1px solid rgba(244, 67, 54, 0.35)">
                      <Typography variant="body2" style={{ fontWeight: 800, color: "#f44336" }}>
                        Faltan datos obligatorios para crear: {formatRutMissingFields(rutOnboardingResult)}.
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Puede completarlos en Correcciones manuales JSON y volver a intentar sin cambiar el PDF original.
                      </Typography>
                    </Box>
                  )}
                  <Typography component="pre" variant="caption" style={{ whiteSpace: "pre-wrap", margin: 0 }}>{JSON.stringify(rutOnboardingResult, null, 2)}</Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRutOnboardingOpen(false)} disabled={rutOnboardingSaving}>Cerrar</Button>
          <Button onClick={() => setRutOnboardingHelpOpen(true)} disabled={rutOnboardingSaving}>Ayuda</Button>
          <Button onClick={clearRutOnboarding} disabled={rutOnboardingSaving}>Limpiar</Button>
          <Button color="primary" variant="outlined" onClick={() => submitRutOnboarding(false)} disabled={rutOnboardingSaving}>{rutOnboardingSaving ? "Procesando..." : "Validar RUT"}</Button>
          <Button color="secondary" variant="contained" onClick={() => submitRutOnboarding(true)} disabled={rutOnboardingSaving || !rutOnboardingResult}>Crear en API</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rutOnboardingHelpOpen} onClose={() => setRutOnboardingHelpOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Guía rápida: alta de empresa por RUT</DialogTitle>
        <DialogContent dividers>
          <Typography paragraph><strong>1. Seleccione la API destino.</strong> Es el servidor donde se creará o completará la empresa.</Typography>
          <Typography paragraph><strong>2. Adjunte y valide el RUT.</strong> Esta primera acción solo lee los datos; no crea nada.</Typography>
          <Typography paragraph><strong>3. Revise el nombre del software y pegue el Software ID DIAN.</strong> El nombre se completa automáticamente. El ID es el UUID de la columna <em>Id</em> de DIAN.</Typography>
          <Typography paragraph><strong>4. Adjunte certificado, logo y resoluciones si aplican.</strong> Las resoluciones PDF se revisan localmente antes del envío.</Typography>
          <Typography paragraph><strong>5. Use opciones técnicas solo si aplica.</strong> Para pruebas de factura son obligatorios Software ID y Set de pruebas DIAN. Use consecutivos nuevos al repetir pruebas.</Typography>
          <Typography paragraph><strong>6. Cree o complete.</strong> Marque la confirmación y use Crear en API. Si la empresa está en habilitación puede completarse; producción bloquea actualizaciones.</Typography>
          <Typography variant="caption" color="textSecondary">Si la API responde un error, el formulario guarda los datos escritos para corregirlos luego. Los archivos se deben adjuntar otra vez por seguridad del navegador.</Typography>
        </DialogContent>
        <DialogActions><Button color="primary" onClick={() => setRutOnboardingHelpOpen(false)}>Entendido</Button></DialogActions>
      </Dialog>

      <Dialog open={clientModalOpen} onClose={() => setClientModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{clientForm.id ? "Editar cliente" : "Nuevo cliente"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                variant="outlined"
                label="Nombre"
                value={clientForm.name}
                onChange={e => setClientForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="NIT / Cedula"
                value={clientForm.identificationNumber}
                onChange={e => setClientForm(prev => ({ ...prev, identificationNumber: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Codigo historico del agente"
                value={clientForm.installationCode}
                onChange={e => setClientForm(prev => ({ ...prev, installationCode: e.target.value }))}
                helperText="Codigo heredado para agentes ya instalados."
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                variant="outlined"
                label="DV"
                value={clientForm.dv}
                onChange={e => setClientForm(prev => ({ ...prev, dv: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                variant="outlined"
                label="Correo principal"
                value={clientForm.email}
                onChange={e => setClientForm(prev => ({ ...prev, email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                variant="outlined"
                label="Telefono principal"
                value={clientForm.phone}
                onChange={e => setClientForm(prev => ({ ...prev, phone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Direccion"
                value={clientForm.address}
                onChange={e => setClientForm(prev => ({ ...prev, address: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="Departamento / Estado"
                value={clientForm.stateName}
                onChange={e => setClientForm(prev => ({ ...prev, stateName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="Matricula mercantil"
                value={clientForm.merchantRegistration}
                onChange={e => setClientForm(prev => ({ ...prev, merchantRegistration: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Estado</InputLabel>
                <Select
                  label="Estado"
                  value={String(clientForm.isActive)}
                  onChange={e => setClientForm(prev => ({ ...prev, isActive: e.target.value === "true" }))}
                >
                  <MenuItem value="true">Activo</MenuItem>
                  <MenuItem value="false">Inactivo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Software</InputLabel>
                <Select
                  label="Software"
                  value={clientForm.softwareClassificationCode}
                  onChange={e => setClientForm(prev => ({ ...prev, softwareClassificationCode: e.target.value }))}
                >
                  <MenuItem value="">Sin definir</MenuItem>
                  {catalogs.software.map(option => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Clasificacion comercial</InputLabel>
                <Select
                  label="Clasificacion comercial"
                  value={clientForm.commercialClassificationCode}
                  onChange={e => setClientForm(prev => ({ ...prev, commercialClassificationCode: e.target.value }))}
                >
                  <MenuItem value="">Sin definir</MenuItem>
                  {catalogs.commercial.map(option => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {Object.entries(CLIENT_EDITABLE_CATALOG_LABELS).map(([catalogType, label]) => (
              <Grid item xs={12} md={6} key={catalogType}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>{label}</InputLabel>
                  <Select
                    label={label}
                    value={String(clientForm.catalogSelections?.[catalogType]?.code || "")}
                    onChange={e => handleEditableCatalogChange(catalogType, e.target.value)}
                  >
                    <MenuItem value="">Sin definir</MenuItem>
                    {getAttributeOptions(catalogType).map(option => (
                      <MenuItem
                        key={`${catalogType}-${option.code || option.name}`}
                        value={String(option.code || option.name || "")}
                      >
                        {option.name || option.code}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ))}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Telefono de notificacion"
                value={clientForm.primaryNotificationPhone}
                onChange={e => setClientForm(prev => ({ ...prev, primaryNotificationPhone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Correo de notificacion"
                value={clientForm.primaryNotificationEmail}
                onChange={e => setClientForm(prev => ({ ...prev, primaryNotificationEmail: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                variant="outlined"
                label="Observaciones"
                value={clientForm.notes}
                onChange={e => setClientForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClientModalOpen(false)} disabled={clientSaving}>
            Cancelar
          </Button>
          <Button onClick={handleSaveClient} color="primary" variant="contained" disabled={clientSaving}>
            {clientSaving ? "Guardando..." : clientForm.id ? "Guardar cambios" : "Crear cliente"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {detailRecord ? `${detailRecord.name} - ${detailRecord.identificationNumber}` : "Detalle del cliente"}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={6}>
              <CircularProgress size={28} />
            </Box>
          ) : detailRecord ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box className={classes.detailSection}>
                  <Grid container spacing={2}>
                    {[
                      ["Contactos vinculados", detailRecord.metrics?.contacts || 0],
                      ["Establecimientos", detailRecord.metrics?.establishments || 0],
                      ["Tickets", detailRecord.metrics?.tickets || 0],
                      ["Tareas", detailRecord.metrics?.tasks || 0]
                    ].map(([label, value]) => (
                      <Grid item xs={6} md={3} key={label}>
                        <Box className={classes.detailMetric}>
                          <Typography variant="overline">{label}</Typography>
                          <Typography variant="h4" style={{ fontWeight: 800, marginTop: 6 }}>
                            {value}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box className={classes.detailSection}>
                  <Typography variant="h6" style={{ fontWeight: 800, marginBottom: 12 }}>
                    Datos principales
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Correo</Typography>
                      <Typography variant="body2">{detailRecord.email || "Sin correo"}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Telefono</Typography>
                      <Typography variant="body2">{detailRecord.phone || "Sin telefono"}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Notificacion</Typography>
                      <Typography variant="body2">
                        {detailRecord.primaryNotificationPhone || detailRecord.primaryNotificationEmail || "Sin dato"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Origen</Typography>
                      <Typography variant="body2">{detailRecord.originCode || "manual"}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">Codigo historico</Typography>
                      <Typography variant="body2">{detailRecord.installationCode || "Sin codigo"}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">Direccion</Typography>
                      <Typography variant="body2">{detailRecord.address || "Sin direccion"}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="textSecondary">Observaciones</Typography>
                      <Typography variant="body2">{detailRecord.notes || "Sin observaciones"}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box className={classes.detailSection}>
                  <Typography variant="h6" style={{ fontWeight: 800, marginBottom: 12 }}>
                    Planes y catalogos importados
                  </Typography>
                  <Box className={classes.compactList}>
                    {(detailRecord.plans || []).length ? detailRecord.plans.map(plan => (
                      <Box key={plan.id} className={classes.compactRow}>
                        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                          {plan.planType ? `Plan ${String(plan.planType).toUpperCase()}` : "Plan importado"}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {plan.planName || "Sin nombre"}{plan.documents ? ` - ${plan.documents} folios/documentos` : ""}{` - inicio ${formatDisplayDate(plan.startDate)}`}
                        </Typography>
                      </Box>
                    )) : (
                      <Typography variant="body2" color="textSecondary">Sin planes importados.</Typography>
                    )}
                    {(detailRecord.catalogValues || []).length ? detailRecord.catalogValues.slice(0, 6).map(item => (
                      <Chip
                        key={item.id}
                        size="small"
                        label={`${CLIENT_CATALOG_LABELS[item.catalogType] || item.catalogType}: ${item.catalogName || item.catalogCode}`}
                      />
                    )) : null}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box className={classes.detailSection}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography variant="h6" style={{ fontWeight: 800 }}>
                      Establecimientos
                    </Typography>
                    <Button
                      size="small"
                      color="primary"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={openCreateEstablishmentModal}
                    >
                      Nuevo
                    </Button>
                  </Box>
                  <Box className={classes.compactList}>
                    {(detailRecord.establishments || []).length ? detailRecord.establishments.map(item => (
                      <Box key={item.id} className={classes.compactRow}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                            {item.name || "Establecimiento sin nombre"}
                          </Typography>
                          <Chip
                            size="small"
                            label={item.isActive === false ? "Inactivo" : "Activo"}
                            color={item.isActive === false ? "default" : "primary"}
                          />
                        </Box>
                        <Typography variant="body2" color="textSecondary">
                          {item.primaryPhone || "Sin telefono"} - {item.primaryEmail || "Sin correo"}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Codigo: {item.installationCode || "Sin codigo"}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {item.address || "Sin direccion"}
                        </Typography>
                        <Box mt={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<EditIcon />}
                            onClick={() => openEditEstablishmentModal(item)}
                          >
                            Editar
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={<DeleteOutlineIcon />}
                            style={{ marginLeft: 8 }}
                            onClick={() => handleDeleteEstablishment(item)}
                          >
                            Eliminar
                          </Button>
                        </Box>
                      </Box>
                    )) : (
                      <Typography variant="body2" color="textSecondary">Sin establecimientos asociados.</Typography>
                    )}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box className={classes.detailSection}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography variant="h6" style={{ fontWeight: 800 }}>
                      Contactos relacionados
                    </Typography>
                    <Button
                      size="small"
                      color="primary"
                      variant="outlined"
                      onClick={handleLinkPossibleContacts}
                    >
                      Relacionar posibles
                    </Button>
                    <Button
                      size="small"
                      color="primary"
                      variant="outlined"
                      style={{ marginLeft: 8 }}
                      onClick={openContactLinkDialog}
                    >
                      Agregar contactos
                    </Button>
                  </Box>
                  <Box className={classes.compactList}>
                    {(detailRecord.contacts || []).length ? detailRecord.contacts.slice(0, 8).map(item => (
                      <Box key={item.id} className={classes.compactRow}>
                        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                          {item.name || "Sin nombre"}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {item.number || "Sin numero"} - {item.email || "Sin correo"}
                        </Typography>
                      </Box>
                    )) : (
                      <Typography variant="body2" color="textSecondary">Sin contactos vinculados.</Typography>
                    )}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box className={classes.detailSection}>
                  <Typography variant="h6" style={{ fontWeight: 800, marginBottom: 12 }}>
                    Tickets y tareas recientes
                  </Typography>
                  <Box className={classes.compactList}>
                    {(detailRecord.tickets || []).slice(0, 5).map(item => (
                      <Box key={`ticket-${item.id}`} className={classes.compactRow}>
                        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                          Ticket #{item.id} - {item.contact?.name || "Sin contacto"}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {item.queue?.name || "Sin cola"} - {item.user?.name || "Sin usuario"} - {item.status}
                        </Typography>
                      </Box>
                    ))}
                    {(detailRecord.operationalTasks || []).slice(0, 5).map(item => (
                      <Box key={`task-${item.id}`} className={classes.compactRow}>
                        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {item.assignedUser?.name || "Sin responsable"} - {item.priority} - {item.status}
                        </Typography>
                      </Box>
                    ))}
                    {!(detailRecord.tickets || []).length && !(detailRecord.operationalTasks || []).length ? (
                      <Typography variant="body2" color="textSecondary">
                        Sin tickets ni tareas relacionadas.
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resolutionDialogOpen}
        onClose={() => !resolutionProcessing && setResolutionDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Subir resolucion DIAN
          <Box component="span" ml={1.5}>
            <Tooltip title="Ayuda del proceso" arrow>
              <IconButton size="small" onClick={() => setResolutionHelpOpen(true)}>
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {resolutionLoading ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress size={28} /></Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box className={classes.detailSection}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                      <Typography variant="subtitle1" style={{ fontWeight: 800 }}>
                        {resolutionCustomer?.name || "Cliente"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        NIT/Cedula: {resolutionCustomer?.identificationNumber || "Sin dato"}
                        {resolutionCustomer?.dv ? `-${resolutionCustomer.dv}` : ""}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary">Procesamiento</Typography>
                      <Typography variant="body2">
                        El endpoint identifica automaticamente la API y el software tecnico.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="caption" color="textSecondary">Software ID</Typography>
                      <Typography variant="body2">Se consulta internamente</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  variant="outlined"
                  type="file"
                  label="PDF oficial de resolucion DIAN"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ accept: "application/pdf,.pdf" }}
                  onChange={handleResolutionFileChange}
                  helperText={resolutionFile ? `Seleccionado: ${resolutionFile.name}` : "PDF legible, sin clave, maximo 8 MB."}
                />
              </Grid>
              {canSyncResolutionToNube(resolutionCustomer) ? (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={syncResolutionToNube}
                        onChange={event => setSyncResolutionToNube(event.target.checked)}
                        color="primary"
                      />
                    }
                    label="Subir tambien a FacilWeb Nube"
                  />
                  <Typography variant="caption" color="textSecondary" display="block">
                    Primero se procesa la resolucion DIAN. Despues se valida en FacilWeb Nube y solo se aplica con su confirmacion.
                  </Typography>
                </Grid>
              ) : null}
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  style={{ height: "100%", minHeight: 54 }}
                  disabled={resolutionProcessing || !resolutionFile}
                  onClick={processResolution}
                >
                  {resolutionProcessing ? "Procesando..." : "Procesar resolucion"}
                </Button>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" style={{ fontWeight: 800, marginTop: 8, marginBottom: 10 }}>
                  Historial de resoluciones
                </Typography>
                <div className={classes.tableWrap}>
                  <Table size="small">
                    <TableHead className={classes.tableHead}>
                      <TableRow>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Usuario</TableCell>
                        <TableCell>Resolucion</TableCell>
                        <TableCell>Prefijo</TableCell>
                        <TableCell>Documento</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell align="right">Detalle</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resolutionHistory.map(item => (
                        <TableRow key={item.id}>
                          <TableCell>{item.updatedAt ? new Date(item.updatedAt).toLocaleString("es-CO") : "Sin fecha"}</TableCell>
                          <TableCell>{item.uploadedByUser?.name || "Sistema"}</TableCell>
                          <TableCell>{item.resolutionNumber || "Pendiente"}</TableCell>
                          <TableCell>{item.prefix || "-"}</TableCell>
                          <TableCell>{item.resolutionType || "Resolucion DIAN"}</TableCell>
                          <TableCell>
                            <Chip size="small" label={item.sourceStatus || "pending"} color={item.sourceStatus === "success" ? "primary" : "default"} />
                          </TableCell>
                          <TableCell align="right" style={{ whiteSpace: "nowrap" }}>
                            <Button size="small" variant="outlined" onClick={() => openResolutionDetail(item)}>Ver</Button>
                            {item.retryRecommended || item.sourceStatus === "pending_dian" ? (
                              <Button size="small" color="primary" disabled={resolutionProcessing} onClick={() => retryResolution(item)}>
                                Reintentar
                              </Button>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                      {!resolutionHistory.length ? (
                        <TableRow><TableCell colSpan={7} align="center">Aun no hay resoluciones procesadas para este cliente.</TableCell></TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </div>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolutionHelpOpen(true)}>Ayuda</Button>
          <Button onClick={() => setResolutionDialogOpen(false)} disabled={resolutionProcessing}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(nubeSyncConfirmation)}
        onClose={() => !nubeSyncApplying && setNubeSyncConfirmation(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar sincronizacion con FacilWeb Nube</DialogTitle>
        <DialogContent dividers>
          <Typography paragraph>
            {nubeSyncConfirmation?.preview?.message || "La resolucion esta lista para sincronizarse con FacilWeb Nube."}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Esta accion aplicara la resolucion en FacilWeb Nube. No se enviara ningun token al navegador ni se ejecutara automaticamente sin su confirmacion.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNubeSyncConfirmation(null)} disabled={nubeSyncApplying}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={applyNubeResolutionSync} disabled={nubeSyncApplying}>
            {nubeSyncApplying ? "Sincronizando..." : "Confirmar y sincronizar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resolutionHelpOpen} onClose={() => setResolutionHelpOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ayuda: subir resolucion DIAN</DialogTitle>
        <DialogContent dividers>
          <Typography paragraph>1. Seleccione el PDF oficial de la resolucion DIAN. No escriba NIT, DV, prefijo, numero de resolucion ni Software ID.</Typography>
          <Typography paragraph>2. Presione Procesar resolucion. SOLUWHATS envia solamente el PDF; el endpoint identifica el cliente, la API y el Software ID tecnico.</Typography>
          <Typography paragraph>3. Si DIAN aun no ha asociado la resolucion al software, el registro quedara pendiente y podra usar Reintentar sin volver a cargar el PDF. Esto no es un error definitivo.</Typography>
          <Typography paragraph>4. Para clientes clasificados como FACILWEB NUBE, puede activar Subir tambien a FacilWeb Nube. El sistema primero hace una validacion sin cambios. Si la informacion esta lista, le pedira confirmacion antes de crear o reemplazar una resolucion en Nube.</Typography>
          <Typography paragraph>En el flujo normal no se abre Chrome ni se manipula el certificado. En un escenario especial futuro, el sistema podria abrir una ventana asistida de DIAN. Si aparece, no la cierre ni la manipule sin indicacion; puede minimizarla mientras termina. Si se solicita una accion manual, siga las instrucciones visibles, como resolver un CAPTCHA o confirmar una asociacion.</Typography>
        </DialogContent>
        <DialogActions><Button onClick={() => setResolutionHelpOpen(false)}>Entendido</Button></DialogActions>
      </Dialog>

      <Dialog open={resolutionDetailOpen} onClose={() => setResolutionDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalle del procesamiento de resolucion</DialogTitle>
        <DialogContent dividers>
          {resolutionDetail ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2">{resolutionDetail.resolution?.visibleMessage || "Sin mensaje"}</Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8, overflowWrap: "anywhere" }}>
                  Archivo: {resolutionDetail.resolution?.fileName || "PDF no disponible"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary">Datos extraidos</Typography>
                <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{JSON.stringify({
                  identificacion: resolutionDetail.resolution?.extractedIdentificationNumber,
                  dv: resolutionDetail.resolution?.extractedDv,
                  resolucion: resolutionDetail.resolution?.resolutionNumber,
                  prefijo: resolutionDetail.resolution?.prefix,
                  rangoDesde: resolutionDetail.resolution?.rangeFrom,
                  rangoHasta: resolutionDetail.resolution?.rangeTo,
                  tipoDocumento: resolutionDetail.resolution?.resolutionType,
                  operacion: resolutionDetail.resolution?.sourceOperation,
                  httpStatus: resolutionDetail.resolution?.sourceHttpStatus
                }, null, 2)}</pre>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary">Respuesta de la API</Typography>
                <pre style={{ maxHeight: 260, overflow: "auto", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{JSON.stringify(resolutionDetail.resolution?.sourceResponsePayloadJson || {}, null, 2)}</pre>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">Historial de intentos</Typography>
                {(resolutionDetail.events || []).map(event => (
                  <Box key={event.id} className={classes.compactRow} mt={1}>
                    <Typography variant="subtitle2">{event.eventType} - {event.performedByUser?.name || "Sistema"}</Typography>
                    <Typography variant="body2">{event.details}</Typography>
                    <Typography variant="caption" color="textSecondary">{event.eventAt ? new Date(event.eventAt).toLocaleString("es-CO") : ""}</Typography>
                  </Box>
                ))}
              </Grid>
            </Grid>
          ) : <Box display="flex" justifyContent="center" py={5}><CircularProgress size={28} /></Box>}
        </DialogContent>
        <DialogActions><Button onClick={() => setResolutionDetailOpen(false)}>Cerrar</Button></DialogActions>
      </Dialog>

      <Dialog
        open={apiOperationsOpen}
        onClose={() => !apiOperationsSaving && setApiOperationsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Ambiente real y planes</DialogTitle>
        <DialogContent dividers>
          {apiOperationsLoading ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress size={28} /></Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box className={classes.detailSection}>
                  <Typography variant="subtitle1" style={{ fontWeight: 800 }}>
                    {apiOperationsCustomer?.name || "Cliente"}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    NIT/Cedula: {apiOperationsCustomer?.identificationNumber || "Sin dato"}
                    {apiOperationsCustomer?.dv ? `-${apiOperationsCustomer.dv}` : ""}
                  </Typography>
                  <Typography variant="body2" style={{ marginTop: 8 }}>
                    {apiOperationsStatus?.message || "Estado operativo aun no consultado."}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 6 }}>
                    Los ambientes se leen desde la API y los cambios se envian a la API con confirmacion. SoluWats queda sincronizado despues de cada operacion.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Tabs
                  value={apiOperationsTab}
                  onChange={(_event, value) => setApiOperationsTab(value)}
                  indicatorColor="primary"
                  textColor="primary"
                >
                  <Tab label="Ambientes" />
                  <Tab label="Planes y folios" />
                </Tabs>
              </Grid>

              {apiOperationsTab === 0 ? <>
              <Grid item xs={12}>
                <Typography variant="h6" style={{ fontWeight: 800 }}>APIs donde aparece</Typography>
                {apiOperationsStatus?.selectedSourceName ? (
                  <Typography variant="body2" color="textSecondary">
                    API principal elegida: {apiOperationsStatus.selectedSourceName}. Si aparece en varias APIs, se usa el registro mas reciente.
                  </Typography>
                ) : null}
                <Box className={classes.compactList} mt={1}>
                  {(apiOperationsStatus?.matches || []).length ? apiOperationsStatus.matches.map(match => (
                    <Box key={`${match.sourceId}-${match.sourceKey}`} className={classes.compactRow}>
                      <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                        {match.sourceName || match.sourceCode}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {match.sourceKey} - {match.company?.name || "Empresa encontrada"}
                      </Typography>
                    </Box>
                  )) : (
                    <Typography variant="body2" color="textSecondary">No se encontro el cliente en APIs activas.</Typography>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" style={{ fontWeight: 800 }}>Ambientes de la API real</Typography>
                <Typography variant="body2" color="textSecondary">
                  Use esto solo cuando quiera enviar el cambio a la API. Para editar solo el dato local, use Editar cliente.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Factura electronica</InputLabel>
                  <Select
                    label="Factura electronica"
                    value={apiEnvironmentForm.invoice_environment}
                    onChange={e => setApiEnvironmentForm(prev => ({ ...prev, invoice_environment: e.target.value }))}
                  >
                    <MenuItem value="">No cambiar ({environmentLabel(catalogEnvironmentValue(apiOperationsCustomer, "environment"))})</MenuItem>
                    <MenuItem value="habilitation">Habilitacion</MenuItem>
                    <MenuItem value="production">Produccion</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Nomina</InputLabel>
                  <Select
                    label="Nomina"
                    value={apiEnvironmentForm.payroll_environment}
                    onChange={e => setApiEnvironmentForm(prev => ({ ...prev, payroll_environment: e.target.value }))}
                  >
                    <MenuItem value="">No cambiar ({environmentLabel(catalogEnvironmentValue(apiOperationsCustomer, "payroll_environment"))})</MenuItem>
                    <MenuItem value="habilitation">Habilitacion</MenuItem>
                    <MenuItem value="production">Produccion</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Documento soporte</InputLabel>
                  <Select
                    label="Documento soporte"
                    value={apiEnvironmentForm.eqdocs_environment}
                    onChange={e => setApiEnvironmentForm(prev => ({ ...prev, eqdocs_environment: e.target.value }))}
                  >
                    <MenuItem value="">No cambiar ({environmentLabel(catalogEnvironmentValue(apiOperationsCustomer, "eqdocs_environment"))})</MenuItem>
                    <MenuItem value="habilitation">Habilitacion</MenuItem>
                    <MenuItem value="production">Produccion</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveApiEnvironments}
                  disabled={apiOperationsSaving || !(apiOperationsStatus?.matches || []).length}
                >
                  {apiOperationsSaving ? "Guardando..." : "Guardar ambientes en API"}
                </Button>
              </Grid>
              </> : null}

              {apiOperationsTab === 1 ? <>
              <Grid item xs={12}>
                <Typography variant="h6" style={{ fontWeight: 800, marginTop: 8 }}>Planes y folios</Typography>
                {apiOperationsPlans?.error ? (
                  <Typography variant="body2" color="error">{apiOperationsPlans.error}</Typography>
                ) : (
                  <pre style={{ maxHeight: 180, overflow: "auto", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                    {JSON.stringify(apiOperationsPlans?.result || apiOperationsPlans || {}, null, 2)}
                  </pre>
                )}
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Servicio</InputLabel>
                  <Select
                    label="Servicio"
                    value={apiPlanForm.service}
                    onChange={e => setApiPlanForm(prev => ({ ...prev, service: e.target.value }))}
                  >
                    <MenuItem value="invoice">Factura electronica</MenuItem>
                    <MenuItem value="payroll">Nomina</MenuItem>
                    <MenuItem value="radian">RADIAN</MenuItem>
                    <MenuItem value="support_document">Documento soporte</MenuItem>
                    <MenuItem value="absolute">Folios absolutos</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Plan ID"
                  value={apiPlanForm.plan_id}
                  onChange={e => setApiPlanForm(prev => ({ ...prev, plan_id: e.target.value }))}
                  helperText="Para planes de type_plans"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Folios absolutos"
                  value={apiPlanForm.absolut_plan_documents}
                  onChange={e => setApiPlanForm(prev => ({ ...prev, absolut_plan_documents: e.target.value }))}
                  helperText="Solo si aplica"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={apiPlanForm.renew}
                      onChange={e => setApiPlanForm(prev => ({ ...prev, renew: e.target.checked }))}
                      color="primary"
                    />
                  }
                  label="Renovar"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Notas"
                  value={apiPlanForm.notes}
                  onChange={e => setApiPlanForm(prev => ({ ...prev, notes: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleAssignApiPlan}
                  disabled={apiOperationsSaving || !(apiOperationsStatus?.matches || []).length}
                >
                  {apiOperationsSaving ? "Enviando..." : "Asignar plan o folios"}
                </Button>
              </Grid>
              </> : null}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => apiOperationsCustomer?.id && refreshApiOperations(apiOperationsCustomer.id)} disabled={apiOperationsLoading || apiOperationsSaving}>
            Actualizar
          </Button>
          <Button onClick={() => setApiOperationsOpen(false)} disabled={apiOperationsSaving}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={establishmentModalOpen}
        onClose={() => setEstablishmentModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {establishmentForm.id ? "Editar establecimiento" : "Nuevo establecimiento"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                variant="outlined"
                label="Nombre"
                value={establishmentForm.name}
                onChange={e => setEstablishmentForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="Codigo historico del agente"
                value={establishmentForm.installationCode}
                onChange={e => setEstablishmentForm(prev => ({ ...prev, installationCode: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Estado</InputLabel>
                <Select
                  label="Estado"
                  value={String(establishmentForm.isActive)}
                  onChange={e =>
                    setEstablishmentForm(prev => ({ ...prev, isActive: e.target.value === "true" }))
                  }
                >
                  <MenuItem value="true">Activo</MenuItem>
                  <MenuItem value="false">Inactivo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Direccion"
                value={establishmentForm.address}
                onChange={e => setEstablishmentForm(prev => ({ ...prev, address: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                options={departmentOptions}
                value={selectedDepartment}
                onChange={(_event, value) => {
                  setEstablishmentForm(prev => ({
                    ...prev,
                    departmentCode: value ? String(value.code) : "",
                    departmentName: value ? String(value.name) : "",
                    municipalityCode: "",
                    municipalityName: ""
                  }));
                }}
                getOptionLabel={option => option?.name || ""}
                isOptionEqualToValue={(option, value) => String(option.code) === String(value.code)}
                noOptionsText="No hay departamentos disponibles"
                renderInput={params => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    label="Departamento"
                    placeholder="Seleccione un departamento"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                options={municipalityOptions}
                value={selectedMunicipality}
                onChange={(_event, value) => {
                  setEstablishmentForm(prev => ({
                    ...prev,
                    municipalityCode: value ? String(value.code) : "",
                    municipalityName: value ? String(value.name) : ""
                  }));
                }}
                getOptionLabel={option => option?.name || ""}
                isOptionEqualToValue={(option, value) => String(option.code) === String(value.code)}
                noOptionsText={establishmentForm.departmentCode ? "No hay municipios disponibles" : "Seleccione primero un departamento"}
                disabled={!establishmentForm.departmentCode}
                renderInput={params => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    label="Municipio"
                    placeholder="Seleccione un municipio"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Telefono principal"
                value={establishmentForm.primaryPhone}
                onChange={e => setEstablishmentForm(prev => ({ ...prev, primaryPhone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Correo principal"
                value={establishmentForm.primaryEmail}
                onChange={e => setEstablishmentForm(prev => ({ ...prev, primaryEmail: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                variant="outlined"
                label="Notas"
                value={establishmentForm.notes}
                onChange={e => setEstablishmentForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEstablishmentModalOpen(false)} disabled={establishmentSaving}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveEstablishment}
            color="primary"
            variant="contained"
            disabled={establishmentSaving}
          >
            {establishmentSaving
              ? "Guardando..."
              : establishmentForm.id
                ? "Guardar cambios"
                : "Crear establecimiento"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={contactLinkDialog}
        onClose={() => {
          setContactLinkDialog(false);
          setContactLinkSelection([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Relacionar contactos al cliente</DialogTitle>
        <DialogContent dividers>
          {contactLinkLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={5}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Autocomplete
              multiple
              fullWidth
              options={contactLinkOptions}
              value={contactLinkSelection}
              onChange={(_event, value) => setContactLinkSelection(value)}
              getOptionLabel={option => buildClientContactLabel(option)}
              isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
              filterSelectedOptions
              noOptionsText="No hay contactos disponibles para relacionar"
              renderInput={params => (
                <TextField
                  {...params}
                  fullWidth
                  variant="outlined"
                  label="Buscar contactos"
                  placeholder="Escriba nombre o numero"
                />
              )}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setContactLinkDialog(false);
              setContactLinkSelection([]);
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleManualAssignContacts}
            color="primary"
            variant="contained"
            disabled={contactLinkLoading}
          >
            Relacionar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Clients;

